import React from "react";
import { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Menu, MenuItem } from "@material-ui/core";
import { ChevronDown } from "lucide-react";

interface DropdownOption<T = string> {
    label: string;
    value: T;
}

interface UniversalDropdownProps<T = string> {
    label: string;
    value: T | "";
    options: DropdownOption<T>[];
    onChange: (value: T) => void;
    className?: string;
}

const useDropdownStyles = makeStyles((theme) => ({
    button: {
        color: "#fff",
        textTransform: "none",
        display: "flex",
        alignItems: "center",
        gap: 4,
        fontSize: 16,
        padding: 0,
    },

    value: {
        fontWeight: 400,
    },

    menuPaper: {
        background: "#121212",
        color: "#fff",
    },

    menuItem: {
        color: "#fff",
        [theme.breakpoints.down("sm")]: {
            fontSize: 12,
            minHeight: 32
        },
    },
}));

export function UniversalDropdown<T extends string | number = string>({
    label,
    value,
    options,
    onChange,
}: UniversalDropdownProps<T>) {
    const classes = useDropdownStyles();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (v?: T) => {
        setAnchorEl(null);
        if (v !== undefined) onChange(v);
    };

    const currentLabel =
        options.find(o => o.value === value)?.label || options[0]?.label;

    return (
        <>
            <Button onClick={handleClick} className={classes.button}>
                <div style={{ width: "max-content" }}>
                    {label}:{" "}
                    <span className={classes.value}>{currentLabel}</span>{" "}
                    <ChevronDown size={16} style={{ position: "relative", top: 3 }} />
                </div>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleClose()}
                classes={{ paper: classes.menuPaper }}
            >
                {options.map(opt => (
                    <MenuItem
                        key={String(opt.value)}
                        selected={opt.value === value}
                        onClick={() => handleClose(opt.value)}
                        className={classes.menuItem}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}